import { useState, useEffect, useCallback, useRef } from 'react';
import { loadCourseProgress, saveProgress, type CourseProgress } from '../services/progressService';

export interface UseProgressReturn {
  completedLessons: Set<number>;
  resumeIndex: number | null;
  loading: boolean;
  markCompleted: (lessonIndex: number, code?: string) => void;
  resetLesson: (lessonIndex: number) => void;
  saveCode: (lessonIndex: number, code: string) => void;
  percentComplete: (totalLessons: number) => number;
}

export function useProgress(courseId: string | undefined): UseProgressReturn {
  const [completedLessons, setCompletedLessons] = useState<Set<number>>(new Set());
  const [resumeIndex, setResumeIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!courseId || loadedRef.current) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const data: CourseProgress | null = await loadCourseProgress(courseId);
      if (cancelled) return;
      if (data && data.totalInteracted > 0) {
        setCompletedLessons(new Set(data.completedLessons));
        const nextIncomplete = findNextIncomplete(data.completedLessons, data.lastLessonIndex);
        setResumeIndex(nextIncomplete);
      }
      setLoading(false);
      loadedRef.current = true;
    })();

    return () => { cancelled = true; };
  }, [courseId]);

  const markCompleted = useCallback((lessonIndex: number, code?: string) => {
    if (!courseId) return;
    setCompletedLessons(prev => {
      if (prev.has(lessonIndex)) return prev;
      const next = new Set(prev);
      next.add(lessonIndex);
      return next;
    });
    saveProgress(courseId, lessonIndex, true, code);
  }, [courseId]);

  const resetLesson = useCallback((lessonIndex: number) => {
    if (!courseId) return;
    setCompletedLessons(prev => {
      if (!prev.has(lessonIndex)) return prev;
      const next = new Set(prev);
      next.delete(lessonIndex);
      return next;
    });
    saveProgress(courseId, lessonIndex, false);
  }, [courseId]);

  const saveCode = useCallback((lessonIndex: number, code: string) => {
    if (!courseId) return;
    saveProgress(courseId, lessonIndex, false, code);
  }, [courseId]);

  const percentComplete = useCallback((totalLessons: number) => {
    if (totalLessons === 0) return 0;
    return Math.round((completedLessons.size / totalLessons) * 100);
  }, [completedLessons]);

  return { completedLessons, resumeIndex, loading, markCompleted, resetLesson, saveCode, percentComplete };
}

function findNextIncomplete(completed: number[], lastIndex: number): number {
  const set = new Set(completed);
  for (let i = 0; i <= lastIndex + 1; i++) {
    if (!set.has(i)) return i;
  }
  return lastIndex + 1;
}
